/**
 * Parse text and split into segments of plain text and URLs.
 * Returns array of { type: 'text' | 'url', value }.
 */
export function parseUrls(text) {
  if (!text) return [];
  const urlRe = /(https?:\/\/[^\s)<>"']+)/g;
  const out = [];
  let last = 0;
  let m;
  while ((m = urlRe.exec(text)) !== null) {
    if (m.index > last) out.push({ type: "text", value: text.slice(last, m.index) });
    out.push({ type: "url", value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: "text", value: text.slice(last) });
  return out;
}

/**
 * Extract unique URLs from a string of text.
 */
export function extractUrls(text) {
  if (!text) return [];
  const urlRe = /(https?:\/\/[^\s)<>"']+)/g;
  return Array.from(new Set((text.match(urlRe) || []).map((u) => u.replace(/[.,;!?]+$/, ""))));
}

/**
 * Render message text with inline clickable links.
 */
export function renderWithLinks(text) {
  return parseUrls(text).map((seg, i) =>
    seg.type === "url" ? (
      <a
        key={i}
        href={seg.value}
        target="_blank"
        rel="noreferrer"
        className="text-[#22D3EE] underline underline-offset-2 break-all hover:text-[#3B82F6]"
      >
        {seg.value}
      </a>
    ) : (
      <span key={i}>{seg.value}</span>
    )
  );
}

/**
 * Lightweight markdown renderer for chat messages.
 * Supports: **bold**, *italic*, `code`, [label](url), naked URLs,
 * `- item` unordered lists, `1. item` ordered lists, and paragraph breaks.
 * Deliberately avoids a heavy dependency — just enough Markdown for chat.
 */
export function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.split("\n");
  const blocks = [];
  let list = null; // { kind: "ul"|"ol", items: [] }

  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw;
    const ul = line.match(/^\s*[-*•]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ul) {
      if (!list || list.kind !== "ul") { flushList(); list = { kind: "ul", items: [] }; }
      list.items.push(ul[1]);
    } else if (ol) {
      if (!list || list.kind !== "ol") { flushList(); list = { kind: "ol", items: [] }; }
      list.items.push(ol[1]);
    } else if (line.trim() === "") {
      flushList();
      blocks.push({ kind: "br" });
    } else {
      flushList();
      blocks.push({ kind: "p", text: line });
    }
  }
  flushList();

  return blocks.map((b, i) => {
    if (b.kind === "br") return <div key={i} className="h-2" aria-hidden="true" />;
    if (b.kind === "ul") {
      return (
        <ul key={i} className="list-disc pl-5 space-y-1 my-2">
          {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    if (b.kind === "ol") {
      return (
        <ol key={i} className="list-decimal pl-5 space-y-1 my-2">
          {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ol>
      );
    }
    return <div key={i}>{renderInline(b.text)}</div>;
  });
}

// Inline formatters — bold, italic, inline-code, markdown links, naked URLs
function renderInline(text) {
  if (!text) return null;
  // Combined regex — capture order: 1=**bold**, 3=`code`, 5=[label](url), 8=naked URL, 9=*italic*/_italic_
  const re = /(\*\*([^*\n]+)\*\*)|(`([^`\n]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))|(https?:\/\/[^\s)<>"']+)|(?<!\*)\*([^*\n]+)\*(?!\*)|_([^_\n]+)_/g;
  const nodes = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    if (m[1]) {
      nodes.push(<strong key={k++} className="font-semibold text-white">{m[2]}</strong>);
    } else if (m[3]) {
      nodes.push(<code key={k++} className="px-1 py-0.5 rounded bg-black/40 border border-white/10 text-[#22D3EE] font-mono text-[12.5px]">{m[4]}</code>);
    } else if (m[5]) {
      nodes.push(
        <a key={k++} href={m[7]} target="_blank" rel="noreferrer"
           className="text-[#22D3EE] underline underline-offset-2 break-all hover:text-[#3B82F6]">
          {m[6]}
        </a>
      );
    } else if (m[8]) {
      nodes.push(
        <a key={k++} href={m[8]} target="_blank" rel="noreferrer"
           className="text-[#22D3EE] underline underline-offset-2 break-all hover:text-[#3B82F6]">
          {m[8]}
        </a>
      );
    } else if (m[9]) {
      nodes.push(<em key={k++} className="italic text-[#E2E8F0]">{m[9]}</em>);
    } else if (m[10]) {
      nodes.push(<em key={k++} className="italic text-[#E2E8F0]">{m[10]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(<span key={k++}>{text.slice(last)}</span>);
  return nodes;
}
